<script setup>
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";

import {
    RIHLogo,
    useUserStore,
    useIntegrationsStore,
    NotificationsDropdown,
    useTariffStore,
} from "@/entities";
import {
    BaseHeaderTemplate,
    BaseButton,
    BaseDropdown,
    IconBars,
    IconLifebuoy,
    // IconBell,
    IconArrowRightFromBracket,
    REPAIR_ROUTE_NAMES,
    vTooltip,
    STORE_ROUTE_NAMES,
    IconComments,
} from "@/shared";

import TheHeaderExitModal from "./TheHeaderExitModal.vue";
const { t } = useI18n();

defineProps({
    isSidebarCollapsed: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["changeSidebarMode"]);

const userStore = useUserStore();
const { integrilla } = storeToRefs(useIntegrationsStore());
const { tariff } = storeToRefs(useTariffStore());

// Тултип заблокированного пункта выносим в body и поднимаем над дропдауном
// (--zi-dropdown: 999999), иначе его режет overflow меню и перекрывает попап.
const tooltipAppendTo = () => document.body;
const TOOLTIP_Z_INDEX = 1000000;

// Разделы Мастерская/Магазин доступны только при наличии модуля в тарифе
// (флаги истинны лишь у «Профи»). Пункт показываем заблокированным с тултипом,
// иначе по клику пользователь уходит на builder и упирается в NO_ACCESS.
const fastCreateLinks = computed(() => {
    const res = [];
    const stockRights = userStore.rights?.STOCK;
    const repairRights = userStore.rights?.REPAIR;

    repairRights?.canAdd &&
        res.push({
            title: t("orders.create_request"),
            link: { name: REPAIR_ROUTE_NAMES.ORDER.ADD },
            disabled: !tariff.value?.haveAccessRepairService,
            tariffTooltip: "Мастерская доступна в тарифе «Профи»",
        });

    stockRights?.canAdd &&
        res.push({
            title: t("orders.process_sale"),
            link: { name: STORE_ROUTE_NAMES.STOCK.ADD },
            disabled: !tariff.value?.haveAccessStore,
            tariffTooltip: "Магазин доступен в тарифе «Профи»",
        });

    return res;
});

// Прокатные пункты (справочник/что нового/обучение Rent in Hand) убраны —
// у Remboard будет своя база знаний. Поддержка — письмом.
const HELP_LINKS = [
    {
        title: t("words.support"),
        link: { name: "REMBOARD_SUPPORT" },
        const: "SUPPORT",
    },
];

const exitModal = ref(null);
</script>

<template>
    <BaseHeaderTemplate
        class="the-header"
        :is-sidebar-collapsed="isSidebarCollapsed"
    >
        <template #logo>
            <RIHLogo :is-collapsed="isSidebarCollapsed" />
        </template>
        <template #content>
            <BaseButton
                class="the-header__collapse-btn the-header__btn"
                variant="white"
                type="square"
                aria-label="Свернуть или развернуть меню"
                @click="$emit('changeSidebarMode')"
            >
                <IconBars />
            </BaseButton>
            <BaseDropdown
                v-if="fastCreateLinks.length > 0"
                class="the-header__fast-create-btn the-header__btn"
                button-variant="secondary"
                button-size="sm"
                button-type="rounded"
                :links="fastCreateLinks"
            >
                <template #button-content> {{ $t("orders.create") }} </template>
                <template #dropdown-content="{ hide }">
                    <li
                        v-for="link in fastCreateLinks"
                        :key="link.title"
                        class="the-header__create-item"
                        :class="{
                            'the-header__create-item--disabled': link.disabled,
                        }"
                    >
                        <RouterLink
                            v-if="!link.disabled"
                            :to="link.link"
                            @click="hide"
                        >
                            {{ link.title }}
                        </RouterLink>
                        <span
                            v-else
                            v-tooltip="{
                                content: link.tariffTooltip,
                                appendTo: tooltipAppendTo,
                                zIndex: TOOLTIP_Z_INDEX,
                            }"
                            class="the-header__create-item-label"
                        >
                            {{ link.title }}
                        </span>
                    </li>
                </template>
            </BaseDropdown>
            <!-- Профиль теперь в футере сайдбара (TheSidebar) — кнопку из шапки убрали -->
            <BaseButton
                v-if="integrilla?.chat && integrilla?.token"
                id="integrilla-button"
                class="the-header__btn"
                variant="primary"
                type="circle"
                v-tooltip.bottom="'Integrilla'"
                aria-label="Открыть чат Integrilla"
                @click="$emit('toggleChat')"
            >
                <IconComments class="the-header__icon" />
            </BaseButton>
            <BaseDropdown
                ref="menuDropdown"
                button-variant="invisible"
                button-type="circle"
                button-class="the-header__btn the-header__btn--ghost"
                :links="HELP_LINKS"
                :tooltip-text="$t('words.support_help')"
            >
                <template #button-content>
                    <IconLifebuoy
                        class="the-header__icon the-header__icon--lifebuoy"
                    />
                </template>
            </BaseDropdown>
            <NotificationsDropdown class="the-header__btn" />
            <BaseButton
                class="the-header__btn the-header__btn--ghost"
                variant="invisible"
                type="circle"
                v-tooltip.bottom="$t('orders.exit')"
                :aria-label="$t('orders.exit')"
                @click="exitModal.show()"
            >
                <IconArrowRightFromBracket class="the-header__icon" />
            </BaseButton>
            <TheHeaderExitModal ref="exitModal" />
        </template>
    </BaseHeaderTemplate>
</template>

<style lang="scss" scoped>
.the-header {
    &__collapse-btn {
        // Бургер: белый фон + тёмные чёрточки. Без видимой рамки (border того же цвета что фон)
        --button-border-color: var(--button-color);
        --button-border-color-hover: var(--button-color);
        --button-width: var(--header-height);
        --button-height: var(--header-height);
        --button-border-radius: 0;

        flex-shrink: 0;

        margin-right: auto;

        ::v-deep(svg) {
            color: var(--c-neutral-70);
        }
    }

    &__fast-create-btn {
        margin-right: 10px;
        margin-left: 10px;
    }

    &__create-item {
        &--disabled {
            cursor: help;

            // Заблокированный Pro-пункт: приглушаем и убираем hover-подсветку
            opacity: 0.5;

            &:hover {
                background-color: transparent !important;
            }
        }
    }

    &__create-item-label {
        display: block;
    }

    &__icon {
        pointer-events: none;

        &--lifebuoy {
            width: 20px !important;
            height: auto;
        }
    }

    &__btn {
        flex-shrink: 0;

        &:active {
            svg {
                color: var(--c-green-10) !important;
            }
        }
    }

    // Ghost-кнопки (Спасательный круг, Выход) — без teal фона, тёмная иконка
    &__btn--ghost {
        --button-color: transparent;
        --button-color-hover: rgba(15 22 36 / 6%);
        --button-color-active: rgba(15 22 36 / 10%);
        --button-border-color: transparent;
        --button-border-color-hover: transparent;
        --button-border-color-active: transparent;

        ::v-deep(svg) {
            color: var(--c-neutral-70);
        }

        &:hover ::v-deep(svg) {
            color: var(--c-text);
        }
    }
}
</style>
